import React from 'react';
import { Brain, Sparkles, ArrowRight, Code2, MessageSquare, Lightbulb, Award, Target } from 'lucide-react';
import { ChatInterface } from './components/ui/ChatInterface';
import { motion } from 'framer-motion';

// Componente de Card reutilizável
const Card = ({ children, className = '' }: { children: React.ReactNode; className?: string }) => (
  <div className={`bg-[#111]/80 backdrop-blur-xl rounded-[20px] p-8 border border-white/[0.08] shadow-2xl ${className}`}>
    {children}
  </div>
);

// Componente de Botão reutilizável
const Button = ({ 
  children, 
  onClick, 
  variant = 'primary',
  className = '' 
}: { 
  children: React.ReactNode; 
  onClick?: () => void; 
  variant?: 'primary' | 'secondary';
  className?: string;
}) => {
  const baseClasses = "h-[48px] px-6 rounded-xl font-everett text-[14px] transition-all flex items-center justify-center gap-2";
  const variants = {
    primary: "bg-white text-black hover:bg-[#F2F2F2]",
    secondary: "bg-white/[0.08] text-white hover:bg-white/[0.12]"
  };
  
  return (
    <button 
      onClick={onClick}
      className={`${baseClasses} ${variants[variant]} ${className}`}
    >
      {children}
    </button>
  );
};

const SplineBackground = () => (
  <div className="absolute inset-0 z-0">
    <div className="relative w-full h-[150vh]">
      <iframe
        src="https://my.spline.design/meeet-5ce2cffd31f30a704ff19b1e1481c7fa/"
        frameBorder="0"
        width="100%"
        height="100%"
        className="absolute inset-0 origin-center scale-[1.2] md:scale-[1.5]"
        title="Spline Background"
        style={{ 
          pointerEvents: 'none',
          transform: 'scale(1.2) translateY(-10%)',
          '@media (min-width: 768px)': {
            transform: 'scale(1.5) translateY(-15%)',
          }
        }}
      />
      {/* Gradiente mais suave para melhor legibilidade */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/40 pointer-events-none" />
    </div>
  </div>
);

// Feature badges section
const FeatureBadges = () => {
  const features = [
    { icon: Lightbulb, label: 'Inovação' },
    { icon: Code2, label: 'Tecnologia' },
    { icon: Target, label: 'Impacto' },
    { icon: Award, label: 'Excelência' },
  ];

  return (
    <div className="relative w-full pb-16 md:pb-32">
      <div className="flex justify-center gap-3 md:gap-4 items-center flex-wrap px-4">
        {features.map(({ icon: Icon, label }) => (
          <div
            key={label}
            className="bg-[#0A0A0A]/80 backdrop-blur-md rounded-xl border border-white/[0.12] px-4 md:px-6 py-2 md:py-3 flex items-center gap-2 text-xs md:text-sm text-white/80 hover:bg-white/[0.08] transition-colors cursor-pointer"
          >
            <Icon className="w-3 h-3 md:w-4 md:h-4" />
            {label}
          </div>
        ))}
      </div>
    </div>
  );
};

export default function Landing() {
  const handleCaptureEmail = (email: string, phone: string, name: string) => {
    console.log('Captured lead:', { email, phone, name });
  };

  return (
    <div className="min-h-screen bg-black text-white relative overflow-hidden landing-page">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Spline Background */}
        <SplineBackground />
        
        {/* Content */}
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8 relative z-10 py-8 md:py-16">
          <div className="max-w-4xl mx-auto">
            {/* Logo */}
            <motion.div 
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-20"
            >
              <img 
                src="/images/Logo branca e icone.svg" 
                alt="LegatIA Logo" 
                className="h-[40px] object-contain mx-auto"
              />
            </motion.div>
            
            {/* Main Title */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center space-y-6 mb-16"
            >
              <h1 className="text-[3.5rem] md:text-[4rem] font-medium tracking-[-0.04em] leading-[0.95]">
                <span className="block text-white drop-shadow-lg">
                  E se sua ideia pudesse
                </span>
                <div className="relative mt-2">
                  <span className="block text-white drop-shadow-lg">
                    Revolucionar o seu mercado?
                  </span>
                </div>
              </h1>

              <p className="text-lg md:text-xl text-white/80 max-w-3xl mx-auto leading-[1.6] font-light drop-shadow-lg">
                Não deixe sua visão ser apenas um sonho. Com a LegatIA, transformamos sua ideia
                em uma solução tecnológica que conecta, inova e impacta.
              </p>
            </motion.div>

            {/* Chat Interface */}
            <motion.div 
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              className="relative mb-16 max-w-[780px] mx-auto"
            >
              <ChatInterface onCaptureEmail={handleCaptureEmail} />
            </motion.div>

            {/* Feature badges */}
            <motion.div 
              initial={{ opacity: 0, y: 60 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-24"
            >
              <FeatureBadges />
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
